import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { useOptionalSession } from './SessionContext';

/**
 * GTMContext provides a pushEvent function for Google Tag Manager event tracking.
 */
interface GTMContextValue {
  pushEvent: (event: string, data?: Record<string, any>) => void;
}

// Default GTM Container ID to use when client doesn't specify one
const DEFAULT_GTM_ID = 'GTM-5QDRNWW';

function noopPushEvent(event: string, data?: Record<string, any>) {
  // Warn if pushEvent is called before GTM is initialized
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      'GTM pushEvent called but GTM is not initialized:',
      event,
      data
    );
  }
}

const GTMContext = createContext<GTMContextValue>({
  pushEvent: noopPushEvent,
});

function initializeGTM(gtmId: string) {
  if (!gtmId || document.getElementById('gtm-script')) return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(script);
}

function gtmTrackEvent(event: string, data: Record<string, any> = {}) {
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ event, ...data });
}

/**
 * GTMProvider initializes GTM when the GTM container ID is available from session,
 * and provides a pushEvent utility to children.
 *
 * Note: session.gtmContainerId is used directly because Session extends Platform.
 * If gtmContainerId is blank or undefined, the default GTM ID will be used.
 */
export const GTMProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { session } = useOptionalSession();
  // Use session gtmContainerId if available, otherwise fall back to default
  const gtmId = session?.gtmContainerId || DEFAULT_GTM_ID;

  useEffect(() => {
    if (gtmId) {
      initializeGTM(gtmId);
    }
  }, [gtmId]);

  const value = useMemo(
    () => ({
      pushEvent: (event: string, data?: Record<string, any>) => {
        if (gtmId) {
          gtmTrackEvent(event, data);
        }
      },
    }),
    [gtmId]
  );

  return <GTMContext.Provider value={value}>{children}</GTMContext.Provider>;
};

/**
 * useGTM provides access to the GTM context.
 */
export function useGTM() {
  return useContext(GTMContext);
}
